import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Form,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  Tooltip
} from "reactstrap";
import uuidv4 from "uuid/v4";
import * as R from "ramda";
import FilterBox from "./filter-box";
import TooltipButton from "../tooltip-button";
import { parseFilters, columnShape } from "./utils";
import { fireEvent } from "../../../utils/google-analytics-handler";

const calculateSelectedValue = (currentFilter, selectedFilterValues, type) => {
  const selectValue = selectedFilterValues.find(x => currentFilter.key === x.key);
  let emptyCase;
  switch (type) {
    case "boolean":
      emptyCase = "all";
      break;
    case "date":
      emptyCase = {};
      break;
    default:
      emptyCase = "";
      break;
  }

  return selectValue ? selectValue.value : emptyCase;
};

const Filters = ({
  activeFilters,
  clearFilters,
  onChipFilterChange,
  onFilterChange,
  data,
  columns,
  enableGoogleAnalytics
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([...activeFilters]);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const filters = useMemo(() => parseFilters(columns, data, selectedFilters), [
    columns,
    data,
    selectedFilters
  ]);
  const updateFilter = newFilter => {
    const current = selectedFilters;
    const index = selectedFilters.findIndex(x => x.key === newFilter.key);
    if (
      index >= 0 &&
      (typeof newFilter.value === "boolean" || Number.isInteger(newFilter.value) || newFilter.value)
    ) {
      current[index] = newFilter;
    } else {
      current.push(newFilter);
      setSelectedFilters([...current]);
    }
  };
  const removeFilter = key => {
    const current = selectedFilters.filter(x => x.key !== key);
    setSelectedFilters([...current]);
  };
  const uuid = useMemo(() => `t${uuidv4().replace(/-/g, "")}`, []);
  const filtersItems = filters.map(x => {
    return (
      <FilterBox
        key={x.key}
        filterRenderer={x.filterRenderer}
        selectedValue={calculateSelectedValue(x, selectedFilters, x.type)}
        onChange={value => updateFilter(value)}
        onClear={id => removeFilter(id)}
        id={x.key}
        label={x.label}
        values={x.options}
        type={x.type}
      />
    );
  });
  useEffect(() => {
    setSelectedFilters(activeFilters);
  }, [activeFilters]);
  const DropDowntoggle = () => setDropdownOpen(!dropdownOpen);
  return (
    !R.isEmpty(filters) && (
      <UncontrolledButtonDropdown
        toggle={e => {
          if (
            e.srcElement &&
            e.srcElement.className &&
            e.srcElement.className.includes &&
            !e.srcElement.className.includes("Mui")
          ) {
            DropDowntoggle();
          }
        }}
        isOpen={dropdownOpen}
      >
        <DropdownToggle
          tag="button"
          id={uuid}
          caret
          className="btn-tool-table"
          onClick={DropDowntoggle}
        >
          <span className="icono-filter" />
          <Tooltip placement="top" isOpen={isOpen} target={uuid} toggle={toggle}>
            Filtrar tabla
          </Tooltip>
        </DropdownToggle>
        <DropdownMenu className="drop-filter">
          <div className="drop-header">
            <h5 className="drop-title">Filtros</h5>
            <TooltipButton
              buttonClassName="btn cp-btn__link cp-btn__medium link"
              tooltipText="Borrar filtros"
              buttonText="Limpiar"
              onClick={() => {
                clearFilters();
                setSelectedFilters([]);
              }}
            />
          </div>
          <div className="drop-body">
            <Form className="col-12">{filtersItems}</Form>
          </div>
          <div className="drop-footer">
            <TooltipButton
              buttonClassName="btn cp-btn cp-btn__small"
              tooltipText="Salir del filtrado"
              buttonText="Cancelar"
              onClick={() => setDropdownOpen(false)}
            />
            <TooltipButton
              buttonClassName="btn cp-btn__cta cp-btn__small"
              tooltipText="Actualizar resultados de la tabla"
              buttonText="Aplicar"
              onClick={() => {
                setDropdownOpen(false);
                onChipFilterChange([...selectedFilters]);
                onFilterChange([...selectedFilters]);
                if (enableGoogleAnalytics) {
                  fireEvent("ConciliationProposal", "FilterTable");
                }
              }}
            />
          </div>
        </DropdownMenu>
      </UncontrolledButtonDropdown>
    )
  );
};

Filters.propTypes = {
  activeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired
    })
  ),
  clearFilters: PropTypes.func,
  onChipFilterChange: PropTypes.func,
  onFilterChange: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  columns: PropTypes.arrayOf(PropTypes.shape(columnShape)).isRequired,
  enableGoogleAnalytics: PropTypes.bool
};

Filters.defaultProps = {
  activeFilters: [],
  clearFilters: () => {},
  onChipFilterChange: () => {},
  onFilterChange: () => {},
  data: [],
  enableGoogleAnalytics: false
};

export default Filters;
